import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba, isColorDark, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function CatalogSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const currency = content.currency || 'S/';
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'grid';
    const [hoveredIdx, setHoveredIdx] = useState(null);

    const INITIAL_ITEMS = 24;
    const ITEMS_PER_PAGE = 24;
    const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS);

    if (items.length === 0) return null;

    const getItemImage = (item) => {
        if (item.image_folder_id && item.image_file_id && mediaFolders[item.image_folder_id]) {
            const found = mediaFolders[item.image_folder_id].find(f => String(f.id) === String(item.image_file_id));
            if (found) return found.image_url;
        }
        return item.image_url || null;
    };

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const cols = config.columns || 3;
    const gridCls = cols === 2 ? 'md:grid-cols-2' : cols === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    const hasDiscount = (item) => item.original_price && parseFloat(item.original_price) > parseFloat(item.price || 0);
    const discountPercent = (item) => hasDiscount(item) ? Math.round((1 - parseFloat(item.price) / parseFloat(item.original_price)) * 100) : 0;
    const defaultBadgeColor = config.item_badge_color || s.accent;
    const discountColor = config.discount_badge_color || config.badge_color || '#EF4444';
    const itemBadgeBg = (item) => item.badge_bg || defaultBadgeColor;
    const itemBadgeText = (item) => item.badge_text_color || (isColorDark(itemBadgeBg(item)) ? '#ffffff' : '#111827');
    const ctaBg = (item) => item.cta_bg || s.accent;
    const ctaText = (item) => item.cta_text_color || (isColorDark(ctaBg(item)) ? '#ffffff' : '#111827');

    // ── List variant ──
    if (variant === 'list') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Catalogo'} subtitle={content.subtitle} config={config} s={s} />
                    <div className="space-y-4 max-w-3xl mx-auto">
                        {items.slice(0, visibleCount).map((item, i) => {
                            const img = getItemImage(item);
                            return (
                                <div key={i} className={`${card} flex items-center gap-4 p-4 transition-all duration-500 hover:-translate-y-0.5 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                                    style={{ transitionDelay: visible ? `${100 + Math.min(i, 10) * 80}ms` : '0ms' }}>
                                    {img && <img src={img} alt={item.name} className="w-20 h-20 object-cover rounded-lg shrink-0" loading="lazy" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-bold text-sm" style={{ color: s.text }}>{item.name}</h3>
                                            {item.badge && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: itemBadgeBg(item), color: itemBadgeText(item) }}>{item.badge}</span>}
                                        </div>
                                        {item.description && <p className="text-xs mt-0.5 line-clamp-1" style={{ color: s.textSub }}>{item.description}</p>}
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <span className="font-bold text-base" style={{ color: s.accent }}>{currency} {item.price}</span>
                                        {hasDiscount(item) && <span className="block text-xs line-through" style={{ color: s.textSub }}>{currency} {item.original_price}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {visibleCount < items.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, items.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({items.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Grid (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Catalogo'} subtitle={content.subtitle} config={config} s={s} />
                <div className={`grid grid-cols-1 ${gridCls} gap-6`}>
                    {items.slice(0, visibleCount).map((item, i) => {
                        const img = getItemImage(item);
                        return (
                            <div key={i} className={`group ${card} overflow-hidden transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${150 + Math.min(i, 10) * 100}ms` : '0ms' }}
                                onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)}>
                                <div className="relative aspect-square overflow-hidden">
                                    {img ? (
                                        <img src={img} alt={item.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.03) }}>
                                            <svg className="w-12 h-12 opacity-20" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
                                        </div>
                                    )}
                                    {item.badge && (
                                        <span className="absolute top-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg" style={{ backgroundColor: itemBadgeBg(item), color: itemBadgeText(item) }}>{item.badge}</span>
                                    )}
                                    {hasDiscount(item) && (
                                        <span className="absolute top-3 right-3 text-[10px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: discountColor, color: isColorDark(discountColor) ? '#ffffff' : '#111827' }}>-{discountPercent(item)}%</span>
                                    )}
                                </div>
                                <div className="p-4">
                                    <h3 className="font-bold text-sm" style={{ color: s.text }}>{item.name}</h3>
                                    {item.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: s.textSub }}>{item.description}</p>}
                                    <div className="flex items-center justify-between mt-3">
                                        <div className="flex items-baseline gap-2">
                                            <span className="font-bold text-lg" style={{ color: s.accent }}>{currency} {item.price}</span>
                                            {hasDiscount(item) && <span className="text-xs line-through" style={{ color: s.textSub }}>{currency} {item.original_price}</span>}
                                        </div>
                                        {item.cta_text && (
                                            <a {...resolveLinkProps(item.cta_link || '#contact')} className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:opacity-90" style={{ backgroundColor: ctaBg(item), color: ctaText(item) }}>{item.cta_text}</a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {visibleCount < items.length && (
                    <div className="text-center mt-8">
                        <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, items.length))}
                            className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                            style={{borderColor: s.accent, color: s.accent}}>
                            Ver mas ({items.length - visibleCount} restantes)
                        </button>
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
